import React, { useMemo, useState } from "react";
import {
  Wallet,
  Briefcase,
  Home,
  Plane,
  Smile,
  PiggyBank,
  Map as MapIcon,
  AlertTriangle,
  CheckCircle2,
  TrendingUp,
  ChevronDown,
  ChevronUp,
  BookOpen,
  ToggleLeft,
  ToggleRight,
  Eye,
} from "lucide-react";
import { fmtMoney } from "@/lib/api";
import { useGst } from "@/context/GstContext";
import { useLang } from "@/context/LangContext";
import GstToggle from "@/components/GstToggle";
import HireUsCTA from "@/components/HireUsCTA";
import EmailResultsCard from "@/components/EmailResultsCard";
import NudgeCard from "@/components/NudgeCard";

/* -------------------------------------------------------------------
   D E F A U L T S  (sensible NZ sole-trader monthly figures)
   ------------------------------------------------------------------- */
const DEFAULT_BUSINESS = [
  { key: "overheads", label: "Overheads", icon: Briefcase, val: 600, hint: "Rent, power, phone, software subs" },
  { key: "cogs", label: "Materials / COGS", icon: Wallet, val: 1500, hint: "Supplies, parts, consumables" },
  { key: "marketing", label: "Marketing & growth", icon: TrendingUp, val: 200, hint: "Ads, website, new tools to grow" },
];

const DEFAULT_PERSONAL = [
  { key: "basics", label: "Basics", icon: Home, val: 2400, hint: "Rent / mortgage, power, groceries" },
  { key: "downtime", label: "Downtime", icon: Plane, val: 250, hint: "Holidays, weekends off — you need a breather" },
  { key: "fun", label: "Fun money", icon: Smile, val: 200, hint: "Small treats. Don't be a martyr." },
  { key: "savings", label: "Savings & investments", icon: PiggyBank, val: 400, hint: "Emergency fund, KiwiSaver, future you" },
];

/* -------------------------------------------------------------------
   V A R I A N C E   helpers
   ------------------------------------------------------------------- */
// For COSTS: actual > planned = bad (over-spending, red).
// For INCOME: actual < planned = bad (shortfall, red).
function varianceTone(planned, actual, isIncome = false) {
  if (actual === 0 || actual === null || actual === undefined) return "none";
  const diff = actual - planned;
  if (Math.abs(diff) < 1) return "ontrack";
  if (isIncome) return diff < 0 ? "bad" : "good";
  return diff > 0 ? "bad" : "good";
}

function VariancePill({ planned, actual, isIncome = false }) {
  if (actual === 0 || actual === null || actual === undefined) return null;
  const diff = actual - planned;
  const tone = varianceTone(planned, actual, isIncome);
  const pct = planned > 0 ? Math.round((diff / planned) * 100) : 0;
  const color =
    tone === "bad"
      ? "text-[var(--error)] border-[var(--error)]/40 bg-[var(--error)]/10"
      : tone === "good"
        ? "text-[var(--primary)] border-[var(--primary)]/40 bg-[var(--primary)]/10"
        : "text-zinc-400 border-[var(--border)] bg-[var(--surface-2)]";
  const sign = diff > 0 ? "+" : "";
  return (
    <span
      className={`inline-flex items-center px-1.5 py-0.5 border text-[10px] font-mono ${color}`}
      title={`Actual vs planned`}
    >
      {sign}
      {fmtMoney(diff)} {planned > 0 ? `(${sign}${pct}%)` : ""}
    </span>
  );
}

/* -------------------------------------------------------------------
   L I N E   I T E M  (supports optional actual column + variance)
   ------------------------------------------------------------------- */
function LineItem({
  item,
  value,
  onChange,
  actualValue,
  onActualChange,
  showActuals,
  testIdPrefix,
}) {
  const Icon = item.icon;
  return (
    <div className="p-4 sm:p-5 border-b last:border-b-0 border-[var(--border)]">
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 bg-[var(--surface-2)] border border-[var(--border)] flex items-center justify-center shrink-0">
          <Icon size={16} className="text-[var(--primary)]" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-mono text-xs uppercase tracking-wider text-zinc-300">
            {item.label}
          </div>
          <div className="text-[10px] font-mono text-zinc-500 mt-0.5 truncate">
            {item.hint}
          </div>
          {showActuals && (
            <div className="mt-2">
              <VariancePill planned={value} actual={actualValue} />
            </div>
          )}
        </div>
        <div className="shrink-0 flex flex-col items-end gap-1.5">
          <div className="flex items-center gap-2">
            {showActuals && (
              <div className="text-[9px] font-mono uppercase tracking-wider text-zinc-500 text-right w-20 sm:w-24">
                Planned
              </div>
            )}
            <div className="relative">
              <span className="absolute left-2 top-1/2 -translate-y-1/2 font-mono text-xs text-zinc-500 pointer-events-none">
                $
              </span>
              <input
                type="number"
                min="0"
                step="any"
                inputMode="numeric"
                value={value}
                onChange={(e) => onChange(Number(e.target.value) || 0)}
                className="input-sharp pl-6 pr-2 py-2 w-20 sm:w-24 text-right font-mono text-sm"
                data-testid={`${testIdPrefix}-${item.key}`}
              />
            </div>
          </div>
          {showActuals && (
            <div className="flex items-center gap-2">
              <div className="text-[9px] font-mono uppercase tracking-wider text-[var(--primary)] text-right w-20 sm:w-24">
                Actual
              </div>
              <div className="relative">
                <span className="absolute left-2 top-1/2 -translate-y-1/2 font-mono text-xs text-zinc-500 pointer-events-none">
                  $
                </span>
                <input
                  type="number"
                  min="0"
                  step="any"
                  inputMode="numeric"
                  value={actualValue}
                  onChange={(e) => onActualChange(Number(e.target.value) || 0)}
                  placeholder="0"
                  className="input-sharp pl-6 pr-2 py-2 w-20 sm:w-24 text-right font-mono text-sm border-[var(--primary)]/50"
                  data-testid={`${testIdPrefix}-${item.key}-actual`}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------
   H O W   I T   W O R K S  (collapsible explainer)
   ------------------------------------------------------------------- */
function HowItWorks() {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-6 border border-[var(--border)] bg-[var(--surface)]" data-testid="budget-how-it-works">
      <button
        type="button"
        onClick={() => setOpen((s) => !s)}
        className="w-full flex items-center justify-between p-4 sm:p-5 hover:bg-[var(--surface-2)] transition-colors"
        data-testid="budget-how-toggle"
      >
        <div className="flex items-center gap-3">
          <BookOpen size={18} className="text-[var(--primary)]" />
          <div className="text-left">
            <div className="label-mono">How this works</div>
            <div className="font-display text-lg mt-0.5">
              Five steps from your income to your back pocket.
            </div>
          </div>
        </div>
        {open ? (
          <ChevronUp size={20} className="text-zinc-400" />
        ) : (
          <ChevronDown size={20} className="text-zinc-400" />
        )}
      </button>

      {open && (
        <div className="border-t border-[var(--border)] p-4 sm:p-6 md:p-8 space-y-6 text-sm leading-relaxed text-zinc-300">
          {/* STEP 1 */}
          <Step
            n={1}
            title="Enter your monthly income"
            body={
              <>
                Put in roughly what you bill clients in a typical month —{" "}
                <strong>gross, before tax, and ex-GST</strong>. Don't average
                your blowout months with your slow months; pick a number that
                represents <em>normal</em>. You can always change it.
              </>
            }
            example="Example: you bill $10,000/month most months → enter 10000."
          />

          {/* STEP 2 */}
          <Step
            n={2}
            title="List your business costs"
            body={
              <>
                These are the dollars you spend just to <em>keep operating</em>.
                Three buckets cover almost everything a sole-trader tradie
                needs:
                <ul className="mt-2 space-y-1.5 pl-1">
                  <li>
                    <strong className="text-[var(--primary)]">Overheads</strong> — your
                    "lights stay on" costs. Rent, power, phone, software subs,
                    accounting, insurance.
                  </li>
                  <li>
                    <strong className="text-[var(--primary)]">Materials / COGS</strong>{" "}
                    — anything you buy to deliver the job. Timber, fittings,
                    parts, paint, fuel that ties directly to work.
                  </li>
                  <li>
                    <strong className="text-[var(--primary)]">Marketing & growth</strong>{" "}
                    — money you spend to <em>get more work</em>. Ads, website,
                    branded gear, new tools that expand what you can quote on.
                  </li>
                </ul>
              </>
            }
            example="Example: $600 overheads + $1,500 materials + $200 marketing = $2,300/month."
          />

          {/* STEP 3 */}
          <Step
            n={3}
            title="Set aside for IRD — income tax AND GST"
            body={
              <>
                IRD wants two slices, not one. Smart sole traders park both the
                day each invoice lands, into separate "tax" and "GST" accounts
                they never touch.
                <ul className="mt-2 space-y-1.5 pl-1">
                  <li>
                    <strong className="text-[var(--primary)]">Income tax (28%)</strong>{" "}
                    — applied to <em>profit</em> (income − business costs). This
                    is what you owe IRD on the money you actually earn.
                  </li>
                  <li>
                    <strong className="text-[var(--secondary)]">GST (15%)</strong>{" "}
                    — only if you're GST-registered. You collect 15% on every
                    invoice; you can claim back GST you paid on business inputs.
                    The net you owe IRD ≈ 15% of (income − business costs).
                  </li>
                </ul>
                <br />
                If you're under the $60k GST threshold, flip the GST toggle off
                — there's no GST to set aside.
              </>
            }
            example={
              "Profit = $10,000 − $2,300 = $7,700. Income tax @ 28% = $2,156. GST @ 15% of net = $1,155. Set aside total: $3,311."
            }
          />

          {/* STEP 4 */}
          <Step
            n={4}
            title="Your personal budget"
            body={
              <>
                Whatever's left after business + tax is your{" "}
                <strong>take-home</strong> — the actual money you can live on.
                Now split it into:
                <ul className="mt-2 space-y-1.5 pl-1">
                  <li>
                    <strong className="text-[var(--primary)]">Basics</strong> — rent /
                    mortgage, power, groceries, transport.
                  </li>
                  <li>
                    <strong className="text-[var(--primary)]">Downtime</strong> — a
                    real number for time off. If you don't budget it, you'll
                    burn out.
                  </li>
                  <li>
                    <strong className="text-[var(--primary)]">Fun money</strong> —
                    guilt-free indulgences. Tiny per month, big for morale.
                  </li>
                  <li>
                    <strong className="text-[var(--primary)]">Savings & investments</strong>{" "}
                    — emergency fund first (aim 3 months of basics), then
                    KiwiSaver, then anything else.
                  </li>
                </ul>
              </>
            }
            example="Take-home $5,544 − Personal $3,250 = $2,294 buffer."
          />

          {/* STEP 5 */}
          <Step
            n={5}
            title="The buffer — your safety cushion"
            body={
              <>
                Buffer is what's left after everything is accounted for. It's
                the single most useful number on this whole screen.
                <ul className="mt-2 space-y-1.5 pl-1">
                  <li>
                    <span className="text-[var(--error)] font-mono">{`< $0`}</span>{" "}
                    — <strong>Shortfall.</strong> The plan doesn't balance. A
                    flat tyre will tip you over.
                  </li>
                  <li>
                    <span className="text-[var(--secondary)] font-mono">{`$0–$500`}</span>{" "}
                    — <strong>Tight.</strong> Survivable, but a slow week hurts.
                  </li>
                  <li>
                    <span className="text-[var(--primary)] font-mono">{`$500+`}</span>{" "}
                    — <strong>Healthy.</strong> Slow weeks won't sink you;
                    surprises don't trigger panic.
                  </li>
                  <li>
                    <span className="text-[var(--primary)] font-mono">{`$1,500+`}</span>{" "}
                    — <strong>Plenty of room.</strong> Money's sitting idle.
                    Boost savings, invest in growth, or pay yourself more.
                  </li>
                </ul>
              </>
            }
          />

          {/* WORKED EXAMPLE BLOCK */}
          <div className="border-l-2 border-[var(--primary)] pl-4 max-w-3xl">
            <div className="label-mono text-[var(--primary)]">Worked example · GST-registered</div>
            <div className="font-mono text-xs text-zinc-300 mt-2 space-y-0.5">
              <div>Income (ex-GST) ........................... $10,000.00</div>
              <div className="text-[var(--error)]">− Business costs ............................. −$2,300.00</div>
              <div>= Profit ..................................... $7,700.00</div>
              <div className="text-[var(--error)]">− Income tax (28% of profit) ............... −$2,156.00</div>
              <div className="text-[var(--error)]">− GST set-aside (15% of profit) ............ −$1,155.00</div>
              <div>= <strong className="text-[var(--primary)]">Take-home ............................. $4,389.00</strong></div>
              <div className="text-[var(--error)]">− Personal spending ......................... −$3,250.00</div>
              <div>= <strong className="text-[var(--primary)]">Buffer ................................ $1,139.00</strong> ✓ Healthy</div>
            </div>
            <div className="mt-3 font-mono text-[10px] text-zinc-500 leading-relaxed">
              Not GST-registered? The GST line goes to $0 and your buffer grows
              accordingly — but remember, you also can't claim GST back on your
              business inputs.
            </div>
          </div>

          {/* ACTUALS */}
          <div className="border border-[var(--border)] p-4">
            <div className="flex items-center gap-2">
              <Eye size={14} className="text-[var(--primary)]" />
              <div className="label-mono">What about actuals?</div>
            </div>
            <p className="mt-2">
              The numbers above are your <strong>plan</strong>. Reality always
              drifts. Flip on <strong>"Track actuals"</strong> at the top of
              the page and you'll get a second column to drop in what really
              happened this month.
            </p>
            <p className="mt-2">
              Each row shows a <strong>variance pill</strong> — green if you
              came in under (or earned more on income), red if you blew the
              budget. The summary band at the bottom tells you whether your
              actual buffer matched your planned one. <em>That's</em> where you
              spot the leak.
            </p>
            <p className="mt-2 text-zinc-500 text-xs font-mono">
              Tip: do this once a month, the day after you pay your card off.
              Takes 5 minutes. Saves you thousands.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

function Step({ n, title, body, example }) {
  return (
    <div className="flex gap-4">
      <div className="shrink-0 w-8 h-8 border border-[var(--primary)] text-[var(--primary)] flex items-center justify-center font-display text-lg">
        {n}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-display text-lg text-zinc-100">{title}</div>
        <div className="mt-2">{body}</div>
        {example && (
          <div className="mt-2 font-mono text-xs text-zinc-500 italic">
            {example}
          </div>
        )}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------
   M A I N
   ------------------------------------------------------------------- */
export default function BudgetCalc() {
  const { gstRegistered } = useGst();
  const { t } = useLang();
  const [income, setIncome] = useState(10000);
  const [taxPct, setTaxPct] = useState(28);
  const [gstPct, setGstPct] = useState(15);
  const [business, setBusiness] = useState(
    Object.fromEntries(DEFAULT_BUSINESS.map((i) => [i.key, i.val])),
  );
  const [personal, setPersonal] = useState(
    Object.fromEntries(DEFAULT_PERSONAL.map((i) => [i.key, i.val])),
  );

  // Actuals
  const [showActuals, setShowActuals] = useState(false);
  const [actualIncome, setActualIncome] = useState(0);
  const [actualBusiness, setActualBusiness] = useState(
    Object.fromEntries(DEFAULT_BUSINESS.map((i) => [i.key, 0])),
  );
  const [actualPersonal, setActualPersonal] = useState(
    Object.fromEntries(DEFAULT_PERSONAL.map((i) => [i.key, 0])),
  );

  const totals = useMemo(() => {
    const businessTotal = Object.values(business).reduce((a, b) => a + b, 0);
    const profit = Math.max(0, income - businessTotal);
    // GST set-aside: net GST owed to IRD ≈ gstPct × (income − business costs)
    // (You collect GST on sales, claim back GST on inputs. Net ≈ pct × net revenue.)
    const gstSetAside = gstRegistered ? Math.max(0, (profit * gstPct) / 100) : 0;
    const taxSetAside = (profit * taxPct) / 100;
    const takeHome = Math.max(0, profit - taxSetAside - gstSetAside);
    const personalTotal = Object.values(personal).reduce((a, b) => a + b, 0);
    const buffer = takeHome - personalTotal;
    return { businessTotal, profit, gstSetAside, taxSetAside, takeHome, personalTotal, buffer };
  }, [income, taxPct, gstPct, gstRegistered, business, personal]);

  const actualTotals = useMemo(() => {
    const businessTotal = Object.values(actualBusiness).reduce((a, b) => a + b, 0);
    const profit = Math.max(0, actualIncome - businessTotal);
    const gstSetAside = gstRegistered ? Math.max(0, (profit * gstPct) / 100) : 0;
    const taxSetAside = (profit * taxPct) / 100;
    const takeHome = Math.max(0, profit - taxSetAside - gstSetAside);
    const personalTotal = Object.values(actualPersonal).reduce((a, b) => a + b, 0);
    const buffer = takeHome - personalTotal;
    return { businessTotal, profit, gstSetAside, taxSetAside, takeHome, personalTotal, buffer };
  }, [actualIncome, taxPct, gstPct, gstRegistered, actualBusiness, actualPersonal]);

  const overspending = totals.buffer < 0;
  const tight = totals.buffer >= 0 && totals.buffer < 500;
  const healthy = totals.buffer >= 500;

  // bar segment % of income
  const seg = (v) => (income > 0 ? Math.min(100, (v / income) * 100) : 0);
  const segBusiness = seg(totals.businessTotal);
  const segGst = seg(totals.gstSetAside);
  const segTax = seg(totals.taxSetAside);
  const segPersonal = seg(Math.min(totals.personalTotal, totals.takeHome));
  const segBuffer = Math.max(0, 100 - segBusiness - segGst - segTax - segPersonal);

  return (
    <div className="stagger-in">
      <div className="label-mono">/ CALCULATOR · 07</div>
      <h1 className="font-display tracking-tight mt-2">{t("budget.title", "Budget Planner")}</h1>
      <p className="text-zinc-400 mt-2 max-w-3xl">
        {t("budget.subtitle", "Your trusty map. Plug in what you earn in a typical month, then plan where every dollar goes — business, tax, personal. If the maths doesn't balance, better to find out here than at year-end.")}
      </p>

      {/* HOW IT WORKS — collapsible explainer */}
      <HowItWorks />

      {/* GST-REGISTERED — moved here so users read the explainer first */}
      <div className="mt-6">
        <GstToggle variant="banner" />
      </div>

      {/* ACTUALS TOGGLE */}
      <div className="mt-6 flex flex-wrap items-center justify-between gap-3 border border-[var(--border)] p-4 bg-[var(--surface)]">
        <div className="flex items-center gap-3 min-w-0">
          <Eye size={16} className="text-[var(--primary)] shrink-0" />
          <div className="min-w-0">
            <div className="label-mono">{t("budget.actuals_eyebrow", "Track actuals")}</div>
            <div className="text-xs text-zinc-400 mt-0.5">
              Add what really happened this month and see the variance per line.
            </div>
          </div>
        </div>
        <button
          type="button"
          onClick={() => setShowActuals((s) => !s)}
          className={`flex items-center gap-2 px-3 py-2 border font-mono text-xs uppercase tracking-wider transition-colors ${
            showActuals
              ? "bg-[var(--primary)] text-[var(--on-primary)] border-[var(--primary)]"
              : "border-[var(--border)] text-zinc-300 hover:border-[var(--primary)]"
          }`}
          data-testid="budget-actuals-toggle"
          aria-pressed={showActuals}
        >
          {showActuals ? <ToggleRight size={16} /> : <ToggleLeft size={16} />}
          {showActuals ? t("budget.actuals_on", "Actuals on") : t("budget.actuals_off", "Actuals off")}
        </button>
      </div>

      {/* INCOME + TAX */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-0 mt-8 border border-[var(--border)] bg-[var(--surface)]">
        <div className="p-5 sm:p-6 border-b lg:border-b-0 lg:border-r border-[var(--border)]">
          <div className="label-mono mb-2">
            {gstRegistered
              ? t("budget.income_label_gst", "Monthly income (gross, ex-GST)")
              : t("budget.income_label_nogst", "Monthly income (you're under the $60k GST threshold)")}
          </div>
          <div className="relative">
            <span className="absolute left-4 top-1/2 -translate-y-1/2 font-mono text-2xl text-zinc-500 pointer-events-none leading-none">
              $
            </span>
            <input
              type="number"
              min="0"
              step="any"
              inputMode="numeric"
              value={income}
              onChange={(e) => setIncome(Number(e.target.value) || 0)}
              className="input-sharp text-2xl py-4"
              style={{ paddingLeft: "2.5rem", paddingRight: "2.5rem" }}
              data-testid="budget-income-input"
            />
          </div>
          <div className="text-[10px] font-mono text-zinc-500 mt-2">
            {t("budget.income_hint", "Roughly what hits your account from clients each month")}
          </div>
          {showActuals && (
            <div className="mt-4 pt-4 border-t border-[var(--border)]">
              <div className="flex items-center justify-between mb-2">
                <div className="label-mono text-[var(--primary)]">Actual income this month</div>
                <VariancePill planned={income} actual={actualIncome} isIncome />
              </div>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 font-mono text-lg text-zinc-500 pointer-events-none leading-none">
                  $
                </span>
                <input
                  type="number"
                  min="0"
                  step="any"
                  inputMode="numeric"
                  value={actualIncome}
                  onChange={(e) => setActualIncome(Number(e.target.value) || 0)}
                  placeholder="0"
                  className="input-sharp text-lg py-3 border-[var(--primary)]/50"
                  style={{ paddingLeft: "2.25rem", paddingRight: "2.25rem" }}
                  data-testid="budget-actual-income"
                />
              </div>
            </div>
          )}
        </div>
        <div className="p-5 sm:p-6 border-b lg:border-b-0 lg:border-r border-[var(--border)]">
          <div className="flex items-center justify-between mb-2">
            <div className="label-mono">{t("budget.income_tax_label", "Income tax set-aside")}</div>
            <span className="font-display text-2xl text-[var(--primary)]" data-testid="budget-tax-pct-value">
              {taxPct}%
            </span>
          </div>
          <input
            type="range"
            min="0"
            max="40"
            step="1"
            value={taxPct}
            onChange={(e) => setTaxPct(Number(e.target.value))}
            className="w-full accent-[var(--primary)]"
            data-testid="budget-tax-slider"
            style={{ height: "8px" }}
          />
          <div className="flex justify-between text-[10px] font-mono text-zinc-500 mt-1">
            <span>0%</span>
            <span>28% (most sole traders)</span>
            <span>40%</span>
          </div>
          <div className="mt-3 p-2 border-l-2 border-[var(--primary)] bg-[var(--bg)]/50">
            <div className="text-[10px] font-mono text-zinc-500 leading-relaxed">
              Applied to <strong className="text-zinc-300">profit</strong> (income − business costs). This is what IRD wants for your income tax.
            </div>
            <div className="font-mono text-sm text-[var(--primary)] mt-1.5" data-testid="budget-tax-amount">
              = {fmtMoney(totals.taxSetAside)} <span className="text-[10px] text-zinc-500">/ month</span>
            </div>
          </div>
        </div>

        <div className="p-5 sm:p-6">
          <div className="flex items-center justify-between mb-2">
            <div className="label-mono">{t("budget.gst_set_label", "GST set-aside")}</div>
            <span
              className={`font-display text-2xl ${gstRegistered ? "text-[var(--secondary)]" : "text-zinc-500"}`}
              data-testid="budget-gst-pct-value"
            >
              {gstRegistered ? `${gstPct}%` : "Off"}
            </span>
          </div>
          <input
            type="range"
            min="0"
            max="15"
            step="1"
            value={gstPct}
            onChange={(e) => setGstPct(Number(e.target.value))}
            disabled={!gstRegistered}
            className="w-full accent-[var(--secondary)] disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="budget-gst-slider"
            style={{ height: "8px" }}
          />
          <div className="flex justify-between text-[10px] font-mono text-zinc-500 mt-1">
            <span>0%</span>
            <span>15% (full rate)</span>
            <span>15%</span>
          </div>
          <div className="mt-3 p-2 border-l-2 border-[var(--secondary)] bg-[var(--bg)]/50">
            {gstRegistered ? (
              <>
                <div className="text-[10px] font-mono text-zinc-500 leading-relaxed">
                  Approx <strong className="text-zinc-300">net GST</strong> owed to IRD (GST collected on sales minus GST paid on inputs). Drop to ~10% if you have heavy GST-bearing purchases.
                </div>
                <div className="font-mono text-sm text-[var(--secondary)] mt-1.5" data-testid="budget-gst-amount">
                  = {fmtMoney(totals.gstSetAside)} <span className="text-[10px] text-zinc-500">/ month</span>
                </div>
              </>
            ) : (
              <div className="text-[10px] font-mono text-zinc-500 leading-relaxed">
                Not GST-registered (under the $60k threshold). Switch on the GST toggle at the top of this page to enable.
              </div>
            )}
          </div>
        </div>
      </div>

      {/* WHERE IT GOES — bar */}
      <div className="mt-8 border border-[var(--border)] p-5 sm:p-6 bg-[var(--surface)]">
        <div className="flex items-center gap-2 mb-3">
          <MapIcon size={14} className="text-[var(--primary)]" />
          <div className="label-mono">{t("budget.where_eyebrow", "Where every dollar goes")}</div>
        </div>
        <div
          className="flex h-8 w-full border border-[var(--border)] overflow-hidden"
          data-testid="budget-bar"
        >
          <div
            style={{ width: `${segBusiness}%`, background: "var(--primary)" }}
            title={`Business ${fmtMoney(totals.businessTotal)}`}
          />
          <div
            style={{ width: `${segTax}%`, background: "var(--secondary)" }}
            title={`Income tax ${fmtMoney(totals.taxSetAside)}`}
          />
          <div
            style={{ width: `${segGst}%`, background: "#d48400" }}
            title={`GST ${fmtMoney(totals.gstSetAside)}`}
          />
          <div
            style={{ width: `${segPersonal}%`, background: "var(--text)" }}
            title={`Personal ${fmtMoney(totals.personalTotal)}`}
          />
          <div
            style={{ width: `${segBuffer}%`, background: "var(--surface-2)" }}
            title="Buffer / unallocated"
          />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-4 text-xs font-mono">
          <div>
            <div className="flex items-center gap-2">
              <span className="w-3 h-3" style={{ background: "var(--primary)" }} />
              <span className="text-zinc-400">Business</span>
            </div>
            <div className="text-zinc-200 mt-1" data-testid="budget-sum-business">
              {fmtMoney(totals.businessTotal)}
            </div>
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="w-3 h-3" style={{ background: "var(--secondary)" }} />
              <span className="text-zinc-400">Income tax</span>
            </div>
            <div className="text-zinc-200 mt-1" data-testid="budget-sum-tax">
              {fmtMoney(totals.taxSetAside)}
            </div>
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="w-3 h-3" style={{ background: "#d48400" }} />
              <span className="text-zinc-400">GST</span>
            </div>
            <div className="text-zinc-200 mt-1" data-testid="budget-sum-gst">
              {fmtMoney(totals.gstSetAside)}
            </div>
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="w-3 h-3" style={{ background: "var(--text)" }} />
              <span className="text-zinc-400">Personal</span>
            </div>
            <div className="text-zinc-200 mt-1" data-testid="budget-sum-personal">
              {fmtMoney(totals.personalTotal)}
            </div>
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 border border-[var(--border)]" style={{ background: "var(--surface-2)" }} />
              <span className="text-zinc-400">Buffer</span>
            </div>
            <div
              className={`mt-1 ${overspending ? "text-[var(--error)]" : "text-[var(--primary)]"}`}
              data-testid="budget-sum-buffer"
            >
              {fmtMoney(totals.buffer)}
            </div>
          </div>
        </div>
      </div>

      {/* TWO BUDGETS — SIDE BY SIDE */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 mt-8 border border-[var(--border)]">
        {/* BUSINESS */}
        <div className="border-b lg:border-b-0 lg:border-r border-[var(--border)]">
          <div className="p-4 sm:p-5 bg-[var(--surface)] border-b border-[var(--border)] flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Briefcase size={14} className="text-[var(--primary)]" />
              <div className="label-mono">{t("budget.section.business", "Business budget")}</div>
            </div>
            <div
              className="font-display text-xl text-[var(--primary)]"
              data-testid="budget-business-total"
            >
              {fmtMoney(totals.businessTotal)}
            </div>
          </div>
          {DEFAULT_BUSINESS.map((item) => (
            <LineItem
              key={item.key}
              item={item}
              value={business[item.key]}
              onChange={(v) => setBusiness((s) => ({ ...s, [item.key]: v }))}
              actualValue={actualBusiness[item.key]}
              onActualChange={(v) => setActualBusiness((s) => ({ ...s, [item.key]: v }))}
              showActuals={showActuals}
              testIdPrefix="budget-biz"
            />
          ))}
        </div>

        {/* PERSONAL */}
        <div>
          <div className="p-4 sm:p-5 bg-[var(--surface)] border-b border-[var(--border)] flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Home size={14} className="text-[var(--primary)]" />
              <div className="label-mono">{t("budget.section.personal", "Personal budget")}</div>
            </div>
            <div
              className="font-display text-xl text-[var(--primary)]"
              data-testid="budget-personal-total"
            >
              {fmtMoney(totals.personalTotal)}
            </div>
          </div>
          {DEFAULT_PERSONAL.map((item) => (
            <LineItem
              key={item.key}
              item={item}
              value={personal[item.key]}
              onChange={(v) => setPersonal((s) => ({ ...s, [item.key]: v }))}
              actualValue={actualPersonal[item.key]}
              onActualChange={(v) => setActualPersonal((s) => ({ ...s, [item.key]: v }))}
              showActuals={showActuals}
              testIdPrefix="budget-pers"
            />
          ))}
        </div>
      </div>

      {/* THE BOTTOM LINE — flow */}
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-0 mt-8 border border-[var(--border)]">
        <Cell label="Income" value={income} mono />
        <Cell label="− Business costs" value={totals.businessTotal} sign="-" />
        <Cell label="− Income tax" value={totals.taxSetAside} sign="-" testId="budget-cell-income-tax" />
        <Cell label="− GST" value={totals.gstSetAside} sign="-" testId="budget-cell-gst" />
        <Cell label="Take-home" value={totals.takeHome} accent testId="budget-takehome" />
        <Cell
          label={overspending ? "Shortfall" : "Buffer left"}
          value={Math.abs(totals.buffer)}
          accent={!overspending}
          error={overspending}
          testId="budget-final-buffer"
          sign={overspending ? "-" : ""}
        />
      </div>

      {/* VERDICT */}
      <div
        className={`mt-6 border p-4 sm:p-5 ${
          overspending
            ? "border-[var(--error)]/40 bg-[var(--error)]/5"
            : tight
              ? "border-[var(--secondary)]/40 bg-[var(--secondary)]/5"
              : "border-[var(--primary)]/40 bg-[var(--primary)]/5"
        }`}
        data-testid="budget-verdict"
      >
        <div className="flex items-start gap-3">
          {overspending ? (
            <AlertTriangle size={20} className="text-[var(--error)] shrink-0 mt-0.5" />
          ) : (
            <CheckCircle2 size={20} className="text-[var(--primary)] shrink-0 mt-0.5" />
          )}
          <div>
            <div className="font-display text-lg">
              {overspending
                ? `You're ${fmtMoney(Math.abs(totals.buffer))} short each month.`
                : tight
                  ? `Tight — only ${fmtMoney(totals.buffer)} buffer.`
                  : `Healthy — ${fmtMoney(totals.buffer)} left over.`}
            </div>
            <p className="text-sm text-zinc-300 mt-1 leading-relaxed">
              {overspending
                ? "Something's gotta give. Either bring in more work, trim business costs, or scale back personal spending. Surprise bills will hurt right now."
                : tight
                  ? "You're scraping by. Aim for a $500+ cushion so a flat tyre or a sick week doesn't push you under."
                  : healthy && totals.buffer > 1500
                    ? "Plenty of room. Consider boosting savings or growth spending — money sitting idle isn't working for you."
                    : "On track. Keep an eye on it monthly so a slow week doesn't sneak up on you."}
            </p>
          </div>
        </div>
      </div>

      {/* PLAN VS ACTUAL SUMMARY (only when actuals enabled) */}
      {showActuals && (
        <div
          className="mt-6 border border-[var(--primary)]/40 bg-[var(--surface)]"
          data-testid="budget-actuals-summary"
        >
          <div className="p-4 sm:p-5 border-b border-[var(--border)] flex items-center gap-2">
            <Eye size={16} className="text-[var(--primary)]" />
            <div className="label-mono">Plan vs Actual this month</div>
          </div>
          <div className="grid grid-cols-3 text-xs font-mono uppercase tracking-wider text-zinc-500 border-b border-[var(--border)]">
            <div className="p-3"> </div>
            <div className="p-3 border-l border-[var(--border)] text-zinc-400">Planned</div>
            <div className="p-3 border-l border-[var(--border)] text-[var(--primary)]">Actual</div>
          </div>
          {[
            { label: "Income", p: income, a: actualIncome, income: true },
            { label: "Business costs", p: totals.businessTotal, a: actualTotals.businessTotal },
            { label: "Income tax", p: totals.taxSetAside, a: actualTotals.taxSetAside },
            { label: "GST set aside", p: totals.gstSetAside, a: actualTotals.gstSetAside },
            { label: "Personal spending", p: totals.personalTotal, a: actualTotals.personalTotal },
            { label: "Buffer left", p: totals.buffer, a: actualTotals.buffer, income: true, accent: true },
          ].map((row, i) => (
            <div
              key={i}
              className={`grid grid-cols-3 text-sm border-b last:border-b-0 border-[var(--border)] ${
                row.accent ? "bg-[var(--surface-2)]" : ""
              }`}
            >
              <div className="p-3 text-zinc-300 font-mono text-xs uppercase tracking-wider flex items-center">
                {row.label}
              </div>
              <div className="p-3 border-l border-[var(--border)] font-mono">
                {fmtMoney(row.p)}
              </div>
              <div className="p-3 border-l border-[var(--border)] font-mono flex items-center justify-between gap-2 flex-wrap">
                <span className={row.accent ? "text-[var(--primary)] font-bold" : ""}>
                  {fmtMoney(row.a)}
                </span>
                <VariancePill planned={row.p} actual={row.a} isIncome={!!row.income} />
              </div>
            </div>
          ))}
          <div className="p-3 sm:p-4 bg-[var(--bg)] text-[10px] font-mono text-zinc-500 leading-relaxed">
            <strong className="text-zinc-300">Reading this:</strong> on costs,{" "}
            <span className="text-[var(--primary)]">green = under budget</span>{" "}
            (you saved money) and{" "}
            <span className="text-[var(--error)]">red = over budget</span> (the
            leak). On income & buffer it flips — green means more, red means
            less. Aim for a green buffer row every month.
          </div>
        </div>
      )}

      {/* WHY BUDGETING MATTERS */}
      <div className="mt-10 border border-[var(--border)] p-5 sm:p-6 md:p-8 bg-[var(--surface)]">
        <div className="label-mono">Why bother with a budget?</div>
        <h3 className="font-display text-2xl mt-2">
          Your trusty map to running a profitable business.
        </h3>
        <p className="text-sm text-zinc-300 mt-3 leading-relaxed max-w-3xl">
          A budget is a plan stating how you intend to earn and spend your money.
          It gives you a clear view of where you stand, so you can make informed
          decisions on the fly — and steer your business towards your goals with
          confidence.
        </p>
        <p className="text-sm text-zinc-300 mt-3 leading-relaxed max-w-3xl">
          As a sole trader, you and your business are intertwined — which means
          you're really managing <strong>two budgets at once</strong>: one for the
          business, one for your personal life. The trick is balancing them.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-0 mt-6 border border-[var(--border)]">
          <div className="p-4 sm:p-5 border-b md:border-b-0 md:border-r border-[var(--border)]">
            <div className="flex items-center gap-2">
              <Briefcase size={14} className="text-[var(--primary)]" />
              <div className="label-mono">Business budget covers</div>
            </div>
            <ul className="mt-3 space-y-2 text-sm text-zinc-300">
              <li><span className="text-[var(--primary)] font-mono">·</span> <strong>Overheads</strong> — rent, power, phone, software subs</li>
              <li><span className="text-[var(--primary)] font-mono">·</span> <strong>Cost of goods sold</strong> — supplies, parts, materials</li>
              <li><span className="text-[var(--primary)] font-mono">·</span> <strong>Marketing & growth</strong> — ads, new tools, expansion</li>
            </ul>
          </div>
          <div className="p-4 sm:p-5">
            <div className="flex items-center gap-2">
              <Home size={14} className="text-[var(--primary)]" />
              <div className="label-mono">Personal budget covers</div>
            </div>
            <ul className="mt-3 space-y-2 text-sm text-zinc-300">
              <li><span className="text-[var(--primary)] font-mono">·</span> <strong>Basics</strong> — rent / mortgage, power, groceries</li>
              <li><span className="text-[var(--primary)] font-mono">·</span> <strong>Downtime</strong> — holidays. Even solo operators need a breather.</li>
              <li><span className="text-[var(--primary)] font-mono">·</span> <strong>Fun money</strong> — small treats, no guilt</li>
              <li><span className="text-[var(--primary)] font-mono">·</span> <strong>Savings & investments</strong> — emergency reserve, KiwiSaver, future you</li>
            </ul>
          </div>
        </div>

        <div className="mt-6 border-l-2 border-[var(--primary)] pl-4 max-w-3xl">
          <div className="label-mono text-[var(--primary)]">Balancing the two</div>
          <p className="text-sm text-zinc-300 mt-2 leading-relaxed">
            If you're <strong>growing the business</strong>, expect a little less
            take-home for a while. If you're <strong>growing the family</strong>{" "}
            (congrats!), scale back business spend.
          </p>
          <p className="text-sm text-zinc-300 mt-2 leading-relaxed">
            Either way: check in monthly, adjust on the fly, cover immediate
            needs <em>and</em> long-term growth, and always keep a cushion for
            surprise expenses.
          </p>
        </div>

        <div className="mt-6 text-xs font-mono text-zinc-500 italic">
          ⚠️ And don't forget tax. Park 25–30% of profit in a separate account
          the day it lands. Future-you sleeps better.
        </div>
      </div>

      <NudgeCard
        show={overspending || tight}
        tone={overspending ? "warning" : "info"}
        title={overspending ? "We can help you find the leak" : "Want a second set of eyes?"}
        body={
          overspending
            ? "If your budget doesn't add up, an accountant pays for themselves in the first month. We'll find what's bleeding cash."
            : "Our Growth plan ($75/mo) includes a monthly budget check-in so a slow patch doesn't catch you out."
        }
        cta="Talk to NZ Taxation Ltd"
        campaign={overspending ? "budget-shortfall" : "budget-tight"}
      />

      <EmailResultsCard
        calculator="budget"
        payload={{
          income,
          tax_pct: taxPct,
          gst_pct: gstRegistered ? gstPct : 0,
          gst_registered: gstRegistered,
          business,
          personal,
          totals: {
            business_total: totals.businessTotal,
            income_tax_set_aside: totals.taxSetAside,
            gst_set_aside: totals.gstSetAside,
            take_home: totals.takeHome,
            personal_total: totals.personalTotal,
            buffer: totals.buffer,
          },
          actuals_enabled: showActuals,
          actuals: showActuals
            ? {
                income: actualIncome,
                business: actualBusiness,
                personal: actualPersonal,
                income_tax: actualTotals.taxSetAside,
                gst_set_aside: actualTotals.gstSetAside,
                buffer: actualTotals.buffer,
              }
            : null,
        }}
        labelKey="email.budget.label"
        descKey="email.budget.desc"
      />

      <HireUsCTA
        campaign="budget-calc"
        headline="Numbers not adding up?"
        subline="That's literally our day job. Tax planning, budgeting, GST returns — let us handle the spreadsheets so you can swing the hammer."
      />
    </div>
  );
}

/* -------------------------------------------------------------------
   C E L L  — bottom-line summary tile
   ------------------------------------------------------------------- */
function Cell({ label, value, accent, error, mono, sign = "", testId }) {
  let color = "text-zinc-100";
  if (accent) color = "text-[var(--primary)]";
  if (error) color = "text-[var(--error)]";
  return (
    <div className="p-4 sm:p-5 border-r last:border-r-0 border-b lg:border-b-0 [&:nth-child(2n)]:border-r-0 lg:[&:nth-child(2n)]:border-r border-[var(--border)]">
      <div className="label-mono">{label}</div>
      <div
        className={`font-display text-2xl mt-2 ${color}`}
        data-testid={testId}
      >
        {sign}
        {fmtMoney(value)}
      </div>
    </div>
  );
}
